import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Paperclip, Upload, Trash2, FileText, FileSpreadsheet, FileImage, File as FileIcon, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${BACKEND}/api/files/${path}`;
const MAX_BYTES = 25 * 1024 * 1024;

const iconFor = (name = "", ct = "") => {
  const n = name.toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)$/.test(n)) return FileImage;
  if (/\.(xlsx?|csv)$/.test(n) || ct.includes("sheet") || ct.includes("csv")) return FileSpreadsheet;
  if (/\.(pdf|docx?|txt|md)$/.test(n) || ct.includes("pdf") || ct.includes("word")) return FileText;
  return FileIcon;
};
const fmtSize = (b) => b == null ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

export default function JobAttachments({ jobId, attachments = [], onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    const added = [];
    for (const file of list) {
      if (file.size > MAX_BYTES) { toast.error(`${file.name} exceeds 25MB`); continue; }
      const fd = new FormData(); fd.append("file", file);
      try {
        const r = await api.post(`/jobs/${jobId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data);
      } catch { toast.error(`Upload failed: ${file.name}`); }
    }
    if (added.length) { onChange([...(attachments || []), ...added]); toast.success(`${added.length} file${added.length > 1 ? "s" : ""} added`); }
    setUploading(false);
  };

  const remove = async (path) => {
    try {
      await api.delete(`/jobs/${jobId}/attachments`, { data: { path } });
      onChange((attachments || []).filter((a) => a.path !== path));
      toast.success("File removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="job-attachments">
      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3">
        <Paperclip className="h-4 w-4" /> Documents &amp; Files ({attachments.length})
      </h2>

      {attachments.length > 0 && (
        <div className="space-y-2 mb-3">
          {attachments.map((a) => {
            const Icon = iconFor(a.filename, a.content_type);
            return (
              <div key={a.path} className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid={`attachment-${a.path}`}>
                <Icon className="h-5 w-5 text-lime-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{a.filename}</p>
                  <p className="text-xs text-slate-400">{fmtSize(a.size)}</p>
                </div>
                <a href={fileUrl(a.path)} target="_blank" rel="noopener noreferrer" download data-testid={`attachment-download-${a.path}`}
                  className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900" title="Download">
                  <Download className="h-4 w-4" />
                </a>
                <button onClick={() => remove(a.path)} data-testid={`attachment-remove-${a.path}`}
                  className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <label className="flex items-center justify-center gap-2 text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-lime-400 hover:text-slate-700 transition-colors">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        <span>{uploading ? "Uploading…" : "Upload files (PDF, Excel, images, docs — max 25MB)"}</span>
        <input type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="attachment-input" />
      </label>
    </div>
  );
}
